
import { InterfaceAttachStatus, InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import {
    getHasCustomPrompt,
    getIsCustomPlaceholder, getLeftScale,
    getUnEditable, isPlaceHolderShow,
} from '@editor/src/page/index/components/canvas/element-types/shape-element/utils';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { getElementRect } from '@editor/src/page/index/components/canvas/element-wrapper/utils';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';

function getIsLeftScale(
    factoryOptions: InterfaceFactoryOptions<IElementBaseModel>,
    placeHolderShow: boolean,
    isEditable: boolean,
) {
    const { model, region, fromScale } = factoryOptions;
    // 无编辑权限且该显示占位符时  不渲染element
    const unEditable = getUnEditable(placeHolderShow, isEditable, region);
    const isCustomPlaceholder = getIsCustomPlaceholder(model);
    return getLeftScale(unEditable, fromScale, model.isPlaceholder, isCustomPlaceholder, region);
}

function getIsPlaceHolderShow(factoryOptions: InterfaceFactoryOptions<IElementBaseModel>) {
    const { model, region } = factoryOptions;
    // 是否是通过ph插入的element
    const isCustomPlaceholder = getIsCustomPlaceholder(model);
    // 用户是否修改过ph的默认显示文案
    const hasCustomPrompt = getHasCustomPrompt(model);

    // 是否显示ph
    return isPlaceHolderShow(
        isCustomPlaceholder,
        false,
        model,
        hasCustomPrompt,
        model.isPlaceholder,
        region,
    );
}

// model.selectable：元素是否为master或layout元素
// factoryOptions.isSelectable：元素非（master且当前文档非只读，非协同通时被别人正在编辑） && 非（空白占位符且不能编辑）
function getIsSelectable(factoryOptions: InterfaceFactoryOptions<IElementBaseModel>) {
    const { model } = factoryOptions;
    return (model.selectable && model.currentEditState <= PPTEditorStateType.SELECTABLE) && !(
        model.isPlaceholder && !model.isCustomPlaceholder
        && model.currentEditState > PPTEditorStateType.EDITABLE
    );
}

function getIsEditable(factoryOptions: InterfaceFactoryOptions<IElementBaseModel>) {
    const { model, fromScale } = factoryOptions;

    // 屏蔽 SmartArt 内的文字编辑，懒得看下面的逻辑，直接加一段逻辑
    if (model.parent?.type === UIModelType.SMART_ART) return false;

    return (model.selectable && model.currentEditState === PPTEditorStateType.EDITABLE
            && !(model as IShapeModel).isTextMath) && !fromScale && !model.isAttachment()
        && ((model.slideRef && !factoryOptions.isMasterEdit)
            || !!factoryOptions.isMasterEdit);  // 非母版编辑情况只有 slide 的子元素可以编辑
}

export function generateStates(factoryOptions: InterfaceFactoryOptions<IElementBaseModel>): InterfaceAttachStatus {
    const { model, fromScale, editingStateManager } = factoryOptions;
    const getIsEditableRes = getIsEditable(factoryOptions);
    const getIsPlaceHolderShowRes = getIsPlaceHolderShow(factoryOptions);
    const getIsLeftScaleRes = getIsLeftScale(factoryOptions, getIsPlaceHolderShowRes, getIsEditableRes);

    // 获取元素frame
    const tableResizing = model.type === UIModelType.TABLE && editingStateManager?.isResizing;
    const frame = getElementRect({ element: model, tableResizing, useAdvHas: false, fromScale });
    return {
        // 元素是否可编辑（可选 && 可编辑 && 非公式 && 非附件）
        isEditable: getIsEditableRes,
        // 元素是否可选
        isSelectable: getIsSelectable(factoryOptions),
        // 当前图片裁剪中
        isCroping: model.selected && editingStateManager?.isCroping,
        // 当前元素预览中
        isPreview: model.selected
            && (factoryOptions.editorAnimPreviewManager?.isAnimPreviewing
                || factoryOptions.previewService?.isPreview),
        // 历史逻辑 没看懂
        isLeftScale: getIsLeftScaleRes,
        // 边框是否显示在元素外围
        isOutSideBorder: model.type === UIModelType.IMAGE,
        // 元素的动态frame（拖动过程会变化）
        frame,
    };
}
